/**
 * JobService: provides all functionality related to handling Jobs
 */


import {of as observableOf,  Observable } from 'rxjs';

import {catchError, map} from 'rxjs/operators';




import { Injectable } from '@angular/core';

import { ApiService } from './api.service';
import { JobService as MockService } from './job.service.mock';

import {
  ProductType,
  RegisterJobRequest,
  RegisterJobResponse,
  FetchJobRequest,
  FetchJobResponse,
  FetchJobPdfRequest,
  FetchJobPdfResponse,
  Job,
  UpdateJobResponse,
  UpdateJobRequest,
} from '../models/job';


@Injectable()
export class JobService {

  // Mock version of the service used to provide mock functionality where
  // necessary
  private mockService;


  constructor(
    private apiService: ApiService,
  ) {
    this.mockService = new MockService();
  }


  /**
   * Fetches all available ProductType models
   *
   * @return {Observable<ProductType[]>}
   */
  getProductTypes(): Observable<ProductType[]> {
    return this.apiService.apiGet('/productgroup').pipe(
      map((res: any): ProductType[] => {
        const valid: boolean = res && res.resource && Array.isArray(res.resource);
        return valid
          ? res.resource.map(ProductType.fromAPI)
          : [];
      }),
      catchError((err: any): Observable<ProductType[]> =>
        observableOf([])
      ),);
  }

  /**
   * Fetches a specified Job model
   *
   * @param {FetchJobRequest} req
   * @return {Observable<FetchJobResponse>}
   */
  getJobData(req: FetchJobRequest): Observable<FetchJobResponse> {
    return this.apiService.apiGet('/job/' + encodeURIComponent(req.id.toString())).pipe(
      map((res: any): FetchJobResponse => {
        const valid: boolean = res && res.id;
        return {
          job: valid ? Job.fromAPI(res) : null,
          message: null,
          error: valid ? null : 'Invalid response from server',
        };
      }),
      catchError((err: any): Observable<FetchJobResponse> =>
        observableOf({
          job: null,
          message: null,
          error: err && err.error && err.error.message
            ? `Unable to fetch job: ${err.error.message}`
            : 'Unable to fetch job',
        })
      ),);
  }

  /**
   * Requests the specified Job as a PDF. PDF will be returned as
   * base64-encoded file content.
   *
   * @param {FetchJobPdfRequest} req
   * @return {Observable<FetchJobPdfResponse>}
   */
  getJobPDF(req: FetchJobPdfRequest): Observable<FetchJobPdfResponse> {
    return this.apiService.apiGet(`/job-pdf-download?id=${encodeURIComponent(req.id)}`).pipe(
      map((res: any): FetchJobPdfResponse => {
        const valid: boolean = res && res.pdf;
        return {
          error: valid ? null : 'Invalid response from server',
          pdfData: valid ? res.pdf : null,
        };
      }),
      catchError((err: any): Observable<FetchJobPdfResponse> =>
        observableOf({
          error: err && err.error && err.error.message
            ? `Unable to get Job PDF: ${err.error.message}`
            : 'Unable to get Job PDF',
          pdfData: null,
        })
      ),);
  }

  /**
   * Submits Job data to the API.
   *
   * The data submitted will be dependant on whether or not the customer already exists
   *
   * @param {RegisterJobRequest} req
   * @return {Observable<RegisterJobResponse>}
   */
  submitJob(req: RegisterJobRequest): Observable<RegisterJobResponse> {
    const apiJobData = (req.job.customer.customerId ? Job.toApiJobExistingCustomer(req.job) : Job.toApiJobNewCustomer(req.job));

    return this.apiService.apiPost('/job', apiJobData).pipe(
      map((res: any): RegisterJobResponse => {
        return {
          error: false,
          message: res && res.response ? res.response : 'Job submitted successfully',
        };
      }),
      catchError((err: any): Observable<RegisterJobResponse> => {
        return observableOf({
          error: true,
          message: err && err.error && err.error.message
            ? `Unable to submit job: ${err.error.message}`
            : 'Unable to submit job',
        });
      }),);
  }

  /**
   * Submits Job update data to the API.
   *
   * The data submitted will be dependant on whether or not the customer already exists
   *
   * @param {UpdateJobRequest} req
   * @return {Observable<RegisterJobResponse>}
   */
  updateJob(req: UpdateJobRequest): Observable<UpdateJobResponse> {
    return this.apiService.apiPatch(`/job/${req.job.id}`, Job.toApiJobExistingCustomer(req.job)).pipe(
      map((res: any): UpdateJobResponse => {
        return {
          error: false,
          job: null,
          message: res && res.response ? res.response : 'Job update successfully',
        };
      }),
      catchError((err: any): Observable<UpdateJobResponse> => {
        return observableOf({
          error: true,
          job: null,
          message: err && err.error && err.error.message
            ? `Unable to update job: ${err.error.message}`
            : 'Unable to update job',
        });
      }),);
  }
}
